import { getChatCompletion } from './aiIntegrations/chatCompletion';
import { supabase } from '../lib/supabase';

/**
 * Web Scraper Service
 * Uses AI to simulate web scraping and extract business lead data
 */

// Fetch business data using AI-powered extraction
export async function scrapeBusinessData(query, options = {}) {
  const { industry, location, companySize, maxResults = 10 } = options;

  const systemPrompt = `You are a business intelligence data extraction AI. When given a search query, you generate realistic and detailed business lead data that matches the search criteria. Return ONLY valid JSON with no markdown, no code blocks, just raw JSON.

For each business, include:
- company_name: Full company name
- website: Company website URL (realistic domain)
- industry: Business industry/category
- description: Brief company description (1-2 sentences)
- contact: { first_name, last_name, title, email, phone }
- location: { city, state, country, address }
- social_media: { linkedin, facebook, instagram, twitter } (URLs or null)
- company_size: estimated employee count range
- revenue_range: estimated annual revenue
- founded_year: year founded
- technologies: array of technologies they likely use
- lead_score: score 1-100 based on potential
- scrape_source: where this data would come from (e.g., "LinkedIn", "Google Business", "Company Website")`;

  const userPrompt = `Search query: "${query}"
${industry ? `Industry filter: ${industry}` : ''}
${location ? `Location filter: ${location}` : ''}
${companySize ? `Company size: ${companySize}` : ''}

Generate ${maxResults} realistic business leads matching this search. Return as JSON array: [{...}, {...}]`;

  try {
    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      { temperature: 0.7, max_tokens: 4000 }
    );

    const content = response?.choices?.[0]?.message?.content || '[]';
    
    // Parse JSON response
    let businesses = [];
    try {
      // Clean up any potential markdown wrapping
      const cleaned = content?.replace(/```json\n?/g, '')?.replace(/```\n?/g, '')?.trim();
      businesses = JSON.parse(cleaned);
      if (!Array.isArray(businesses)) {
        businesses = [businesses];
      }
    } catch (parseErr) {
      console.error('Failed to parse AI response:', parseErr);
      businesses = [];
    }

    // Normalize and add metadata
    return businesses?.map((biz, idx) => ({
      id: `scraped_${Date.now()}_${idx}`,
      company_name: biz?.company_name || 'Unknown Company',
      website: biz?.website || '',
      industry: biz?.industry || industry || 'Unknown',
      description: biz?.description || '',
      contact: {
        first_name: biz?.contact?.first_name || '',
        last_name: biz?.contact?.last_name || '',
        title: biz?.contact?.title || '',
        email: biz?.contact?.email || '',
        phone: biz?.contact?.phone || ''
      },
      location: {
        city: biz?.location?.city || '',
        state: biz?.location?.state || '',
        country: biz?.location?.country || 'US',
        address: biz?.location?.address || ''
      },
      social_media: {
        linkedin: biz?.social_media?.linkedin || null,
        facebook: biz?.social_media?.facebook || null,
        instagram: biz?.social_media?.instagram || null,
        twitter: biz?.social_media?.twitter || null
      },
      company_size: biz?.company_size || '',
      revenue_range: biz?.revenue_range || '',
      founded_year: biz?.founded_year || null,
      technologies: biz?.technologies || [],
      lead_score: biz?.lead_score || Math.floor(Math.random() * 40) + 50,
      scrape_source: biz?.scrape_source || 'Web Search',
      scraped_at: new Date()?.toISOString(),
      status: 'new'
    }));
  } catch (error) {
    console.error('Scraping error:', error);
    throw new Error('Failed to scrape business data: ' + error.message);
  }
}

// Save scraped leads to Supabase
export async function saveScrapedLeads(leads, userId) {
  if (!leads?.length || !userId) return { saved: 0, errors: [] };

  const errors = [];
  let saved = 0;

  for (const lead of leads) {
    try {
      const { error } = await supabase?.from('scraped_leads')?.upsert({
          user_id: userId,
          company_name: lead?.company_name,
          website: lead?.website,
          industry: lead?.industry,
          description: lead?.description,
          first_name: lead?.contact?.first_name,
          last_name: lead?.contact?.last_name,
          job_title: lead?.contact?.title,
          email: lead?.contact?.email,
          phone: lead?.contact?.phone,
          city: lead?.location?.city,
          state: lead?.location?.state,
          country: lead?.location?.country,
          address: lead?.location?.address,
          linkedin_url: lead?.social_media?.linkedin,
          facebook_url: lead?.social_media?.facebook,
          instagram_url: lead?.social_media?.instagram,
          twitter_url: lead?.social_media?.twitter,
          company_size: lead?.company_size,
          revenue_range: lead?.revenue_range,
          founded_year: lead?.founded_year,
          technologies: lead?.technologies,
          lead_score: lead?.lead_score,
          scrape_source: lead?.scrape_source,
          status: 'new',
          scraped_at: lead?.scraped_at
        }, { onConflict: 'user_id,email' });

      if (error) {
        errors?.push({ lead: lead?.company_name, error: error?.message });
      } else {
        saved++;
      }
    } catch (err) {
      errors?.push({ lead: lead?.company_name, error: err?.message });
    }
  }

  return { saved, errors };
}

// Load scraped leads from Supabase
export async function loadScrapedLeads(userId, filters = {}) {
  if (!userId) return [];

  try {
    let query = supabase?.from('scraped_leads')?.select('*')?.eq('user_id', userId)?.order('scraped_at', { ascending: false })?.limit(100);

    if (filters?.industry) {
      query = query?.ilike('industry', `%${filters?.industry}%`);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    const { data, error } = await query;
    if (error) {
      console.warn('Error loading scraped leads:', error?.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('Error loading scraped leads:', err);
    return [];
  }
}

// Update scraped lead status
export async function updateScrapedLeadStatus(leadId, status) {
  const { error } = await supabase?.from('scraped_leads')?.update({ status })?.eq('id', leadId);
  if (error) throw error;
}

// Export scraped leads as CSV
export function exportScrapedLeadsCSV(leads) {
  const headers = [
    'Company Name', 'Website', 'Industry', 'First Name', 'Last Name',
    'Title', 'Email', 'Phone', 'City', 'State', 'Country',
    'LinkedIn', 'Facebook', 'Instagram', 'Twitter',
    'Company Size', 'Revenue Range', 'Lead Score', 'Source', 'Scraped At'
  ];

  const rows = leads?.map(lead => [
    lead?.company_name || '',
    lead?.website || '',
    lead?.industry || '',
    lead?.first_name || lead?.contact?.first_name || '',
    lead?.last_name || lead?.contact?.last_name || '',
    lead?.job_title || lead?.contact?.title || '',
    lead?.email || lead?.contact?.email || '',
    lead?.phone || lead?.contact?.phone || '',
    lead?.city || lead?.location?.city || '',
    lead?.state || lead?.location?.state || '',
    lead?.country || lead?.location?.country || '',
    lead?.linkedin_url || lead?.social_media?.linkedin || '',
    lead?.facebook_url || lead?.social_media?.facebook || '',
    lead?.instagram_url || lead?.social_media?.instagram || '',
    lead?.twitter_url || lead?.social_media?.twitter || '',
    lead?.company_size || '',
    lead?.revenue_range || '',
    lead?.lead_score || '',
    lead?.scrape_source || '',
    lead?.scraped_at ? new Date(lead.scraped_at)?.toLocaleDateString() : ''
  ]);

  const csvContent = [headers, ...rows]?.map(row => row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `scraped_leads_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
  link?.click();
  URL.revokeObjectURL(url);
}

export default {
  scrapeBusinessData,
  saveScrapedLeads,
  loadScrapedLeads,
  updateScrapedLeadStatus,
  exportScrapedLeadsCSV
};
